import { useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetcher, mutation } from '../utils/api';
import { ApiRoutes } from '../constants/routes';
import { useAuthProvider } from '../utils/auth';

export const useLibrary = () => {
  const queryClient = useQueryClient();

  // Get auth state
  const { user, isAuthed } = useAuthProvider();

  // Debug logging for auth state
  useEffect(() => {
    console.log('useLibrary: Auth state changed:', { 
      user: !!user, 
      userId: user?.id || user?.uuid,
      isAuthed,
      userObject: user 
    });
  }, [user, isAuthed]);

  // Fetch library data
  const { 
    data: libraryData = { 
      tracks: [], 
      albums: [], 
      playlists: [],
      followed_creators: [],
      followed_curators: [],
      followed_acts: []
    }, 
    isLoading, 
    error,
    refetch: fetchUserLibrary 
  } = useQuery({
    queryKey: ['user-library'],
    queryFn: async () => {
      console.log('useLibrary: Fetching library data...');
      console.log('useLibrary: Auth state:', { user: !!user, isAuthed, userId: user?.id });
      
      // Check if user is properly authenticated
      if (!user || !isAuthed) {
        throw new Error('User not authenticated');
      }
      
      try {
        const data = await fetcher(ApiRoutes.Library);
        console.log('useLibrary: Library data response:', data);
        return data.library || { 
          tracks: [], 
          albums: [], 
          playlists: [],
          followed_creators: [],
          followed_curators: [],
          followed_acts: []
        };
      } catch (error) {
        console.error('useLibrary: Error fetching library data:', error);
        throw error;
      }
    },
    enabled: !!user && isAuthed, // Only fetch when user is authenticated
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    retry: 1,
    retryDelay: 1000, // Wait 1 second before retry
  });


  // Toggle library item mutation
  const toggleLibraryMutation = useMutation(
    async ({ contentType, objectId }) => {
      console.log('useLibrary: Toggling library item:', { contentType, objectId });
      try {
        const data = await mutation(ApiRoutes.LibraryToggle, {
          content_type: contentType,
          object_id: objectId
        });
        console.log('useLibrary: Toggle response:', data);
        return data;
      } catch (error) {
        console.error('useLibrary: Toggle error:', error);
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        console.log('useLibrary: Toggle successful, invalidating queries...');
        // Invalidate and refetch library data
        queryClient.invalidateQueries(['user-library']);
      },
      onError: (err) => {
        console.error('useLibrary: Error toggling library item:', err);
      }
    }
  );

  const toggleLibraryItem = useCallback(async (contentType, objectId) => {
    return toggleLibraryMutation.mutateAsync({ contentType, objectId });
  }, [toggleLibraryMutation]);

  // Get library status query
  const getLibraryStatus = useCallback(async (contentType, objectId) => {
    try {
      const data = await fetcher(`${ApiRoutes.LibraryStatus}${contentType}/${objectId}/`);
      return data;
    } catch (err) {
      console.error('Error getting library status:', err);
      return { is_saved: false, total_saves: 0 };
    }
  }, []);

  const isItemInLibrary = useCallback((contentType, objectId) => {
    const items = libraryData[contentType + 's'] || [];
    return items.some(item => item.id === parseInt(objectId));
  }, [libraryData]);

  return {
    libraryData,
    isLoading,
    error,
    fetchUserLibrary,
    toggleLibraryItem,
    getLibraryStatus,
    isItemInLibrary
  };
};
